<template>
  <div>
    <HeadSection title="Fortune wheel" permissionOfSection="fortune_wheel">
      <template #actions>
        <a-col :span="14" class="d-flex justify-content-end" style="gap: 10px;">
          <a-button
            v-if="weightEditingMode"
            type="danger"
            ghost
            size="large"
            icon="stop"
            :disabled="changeWeighLoading"
            style="font-size: 16px; font-weight: 300; border-radius: 2px;"
            @click="cancelChangeWheelWeight"
          />
          <a-button
            v-if="isHasWheelListItems"
            type="primary"
            size="large"
            class="success-color"
            :loading="changeWeighLoading"
            style="font-size: 16px; font-weight: 300; border-radius: 2px;"
            @click="changeWheelWeight"
          >
            {{ weightEditingMode ? 'Save' : 'Edit' }}
          </a-button>
          <a-button
            type="primary"
            size="large"
            :disabled="weightEditingMode"
            style="font-size: 16px; font-weight: 300; border-radius: 2px;"
            @click="createFortuneWheel"
          >
            <a-icon type="plus" /> Add
          </a-button>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" permissionOfSection="fortune_wheel">
      <div class="d-flex flex-wrap mb-3" style="gap: 10px;">
        <MainFortuneWheelFilters
          ref="wheel-filter"
          :loading="loading"
          :disabled="!hasWheelsListData"
          :appsOptions="appsOptions"
          :languageOptions="languageOptions"
          :typeOptions="typeOptions"
          :segmentOptions="segmentOptions"
          @change="changeFilters"
        />
        <div
          v-if="isFiltered"
          style="width: fit-content; flex: 1 1 auto;"
          class="d-flex justify-content-end align-items-end"
        >
          <a-button
            size="large"
            @click="resetAllFilters"
          >
            Reset filters
          </a-button>
        </div>
      </div>
      <a-table
        class="fortune-wheel-table"
        :class="{'fortune-wheel-table__edit-mode' : weightEditingMode}"
        :dataSource="wheelsListData"
        :pagination="wheelsListMeta"
        :row-key="(record) => record.id"
        :customRow="customRow"
        :columns="columnsComputed"
        :loading="loading"
        @change="changePagination"
      >
        <template slot="name" slot-scope="name, record">
          <div class="wheel-link">
            <router-link class="text-primary" :to="`/fortune-wheel/${record.id}`">{{ name }}</router-link>
          </div>
        </template>
        <template slot="type" slot-scope="type">
          {{ type ? getTypeName(type) : '—' }}
        </template>
        <template slot="segment" slot-scope="segment">
          {{ segment ? segment : '—' }}
        </template>
        <template slot="starts_at" slot-scope="starts_at">
          {{ starts_at ? format(starts_at) : '—' }}
        </template>
        <template slot="ends_at" slot-scope="ends_at">
          {{ ends_at ? format(ends_at) : '—' }}
        </template>
        <template slot="weight" slot-scope="weight, record">
          <div class="change-weight d-flex align-items-center">
            <a-input-number
              v-if="editPermission"
              v-model="record.weight"
              :disabled="!weightEditingMode || changeWeighLoading"
              type="number"
              :max="30000"
              class="transparent-input"
              :class="weightEditingMode ? 'transparent-input__focus' : 'transparent-input__hover-pointer'"
              :loading="changeWeighLoading"
            />
            <span v-else>{{ record.weight }}</span>
          </div>
        </template>
        <template slot="status" slot-scope="status">
          <div class="d-flex align-items-center justify-content-end">
            <span
              style="text-transform: capitalize;"
              class="d-flex mr-2"
              :class="status === 'active' ? '' : 'text-default'"
            >
              {{ status }}
            </span>
          </div>
        </template>
      </a-table>
    </MainContainer>
  </div>
</template>

<script>
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import apiClient from '@/services/axios'
import MainFortuneWheelFilters from '@/views/apps/fortune-wheel/components/MainFortuneWheelFilters.vue'
import { mapState } from 'vuex'
import { waitForRef } from '@/composables/useWaitForRef.js'
import { UTCFormatInCurrentTimezone, currentTimezoneFormatInUTC } from '@/views/apps/fortune-wheel/composables/useFormatDate.js'
import { setQueryParams, setFilters } from '@/composables/useSetQueryParams.js'
import { capitalizeFirstLetter } from '@/composables/useCapitalizeFirstLetter.js'

const wheelApiUrl = process.env.VUE_APP_WHEEL_API_URL

export default {
  props: {
    editPermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    MainFortuneWheelFilters,
  },
  data: () => ({
    fortuneWheelColumns: [
      {
        title: 'ID',
        dataIndex: 'id',
        width: '5%',
        scopedSlots: { customRender: 'id' },
      },
      {
        title: 'Name',
        dataIndex: 'name',
        width: '20%',
        ellipsis: true,
        scopedSlots: { customRender: 'name' },
      },
      {
        title: 'Wheel type',
        dataIndex: 'type',
        width: '10%',
        scopedSlots: { customRender: 'type' },
      },
      {
        title: 'Segment',
        dataIndex: 'segment',
        width: '15%',
        scopedSlots: { customRender: 'segment' },
      },
      {
        title: 'Start date',
        dataIndex: 'starts_at',
        width: '15%',
        scopedSlots: { customRender: 'starts_at' },
      },
      {
        title: 'End date',
        dataIndex: 'ends_at',
        width: '15%',
        scopedSlots: { customRender: 'ends_at' },
      },
      {
        title: 'Weight',
        dataIndex: 'weight',
        width: '10%',
        scopedSlots: { customRender: 'weight' },
        sorter: true,
        sortOrder: undefined,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        align: 'right',
        width: '10%',
        scopedSlots: { customRender: 'status' },
        sorter: true,
        sortOrder: undefined,
      },
    ],
    wheelsListData: [],
    wheelsListMeta: {
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '40'],
    },
    wheelWeightStateSnapshot: [],
    weightEditingMode: false,
    changeWeighLoading: false,
    loading: false,
    appsOptions: [],
    languageOptions: [],
    typeOptions: [],
    segmentOptions: [],
    filters: {
      page: undefined,
      limit: undefined,
      sort_by: undefined,
      sort_direction: undefined,
    },
    isFiltered: false,
  }),
  computed: {
    ...mapState(['user']),
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isHasWheelListItems() {
      return !!this.wheelsListData?.length
    },
    hasWheelsListData() {
      return this.isHasWheelListItems || this.isFiltered
    },
    columnsComputed() {
      return this.fortuneWheelColumns.map((col) => {
        if (col.dataIndex === 'weight' || col.dataIndex === 'status') {
          return {
            ...col,
            sortOrder:
              this.filters.sort_by === col.dataIndex
                ? this.filters.sort_direction === 'asc'
                  ? 'ascend'
                  : 'descend'
                : false,
          }
        }
        return col
      })
    },
  },
  methods: {
    customRow(wheel) {
      return {
        on: {
          click: (e) => {
            if (this.weightEditingMode) {
              e.preventDefault()
              return false
            }
            this.$router.push(`/fortune-wheel/${wheel.id}`)
          },
        },
      }
    },
    format(date) {
      return UTCFormatInCurrentTimezone(date, 'YYYY-MM-DD HH:mm', 'DD.MM.YYYY HH:mm')
    },
    getTypeName(type) {
      return capitalizeFirstLetter(type)
    },
    changeWheelWeight() {
      if (this.weightEditingMode) {
        this.saveWheelWeight()
      } else {
        this.weightEditingMode = true
      }
    },
    cancelChangeWheelWeight(update = true) {
      if (update) {
        this.wheelWeightStateSnapshot.forEach((el, index) => {
          this.wheelsListData[index].weight = el.position
        })
      }
      this.weightEditingMode = false
    },
    async saveWheelWeight() {
      try {
        this.changeWeighLoading = true
        const data = this.wheelsListData.map(wheel => {
          return {
            id: wheel.id,
            position: wheel.weight,
          }
        })
        const url = `${wheelApiUrl}/admin/wheels/batch`
        const response = await apiClient.post(url, data)
        if (response?.data?.success) {
          this.setWheelWeightStateSnapshot(this.wheelsListData)
          this.cancelChangeWheelWeight(false)
          this.$notification.success({
            message: response?.data?.message || 'Successfully',
          })
        }
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.changeWeighLoading = false
      }
    },
    createFortuneWheel() {
      this.$router.push('/fortune-wheel/create')
    },
    async getOtherFilters() {
      return await waitForRef('wheel-filter', this)
        .then((filtersRef) => {
          return filtersRef.getFilters()
        })
        .catch((err) => {
          console.warn('Не удалось получить $refs.filters:', err)
        })
    },
    changeFilters(otherFilters) {
      const filters = { ...this.filters, ...otherFilters }

      this.setQuery(filters)

      this.fetchWheelsList(filters)
    },
    async changePagination(pagination, _, sorter) {
      this.cancelChangeWheelWeight()

      this.filters.page = pagination.current
      this.filters.limit = pagination.pageSize

      if (sorter && sorter?.order) {
        this.filters.sort_by = sorter.field
        this.filters.sort_direction = this.getSortDirectionParam(sorter.order)
      } else {
        this.filters.sort_by = undefined
        this.filters.sort_direction = undefined
      }

      const otherFilters = await this.getOtherFilters()

      const filters = {
        ...this.filters,
        ...otherFilters,
      }

      this.setQuery(filters)

      this.fetchWheelsList(filters)
    },
    setQuery(filters) {
      const queryParams = {
        ...filters,
        ...(filters.starts_at ? { startDate: filters.starts_at } : {}),
        ...(filters.ends_at ? { endDate: filters.ends_at } : {}),
      }
      delete queryParams.starts_at
      delete queryParams.ends_at
      setQueryParams(queryParams, this)
    },
    getSortDirectionParam(order) {
      if (order === 'ascend') {
        return 'asc'
      }
      return 'desc'
    },
    getTableMeta(meta) {
      return {
        current: meta?.current_page || 1,
        pageSize: meta?.per_page || 10,
        total: meta?.total || 0,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '40'],
      }
    },
    checkFilters(filters) {
      if (filters === null) {
        return false
      }
      return !!filters?.applications ||
        !!filters?.languages ||
        (filters?.segment ? filters.segment : filters?.segment === 0) ||
        !!filters?.starts_at ||
        !!filters?.ends_at ||
        !!filters?.status ||
        filters.name?.length ||
        !!filters.sort_by ||
        !!filters.sort_direction ||
        (this.filters.page && this.filters.page > 1)
    },
    setWheelWeightStateSnapshot(data) {
      this.wheelWeightStateSnapshot = data.map(el => {
        return {
          id: el.id,
          position: el.weight,
        }
      })
    },
    async fetchWheelsList(filters = null) {
      this.loading = true

      if (this.checkFilters(filters)) {
        this.isFiltered = true
        if (filters.starts_at) {
          filters.starts_at = currentTimezoneFormatInUTC(filters.starts_at)
        }
        if (filters.ends_at) {
          filters.ends_at = currentTimezoneFormatInUTC(filters.ends_at)
        }
      } else {
        this.isFiltered = false
      }

      const url = `${wheelApiUrl}/admin/wheels`

      try {
        const response = await apiClient.get(url, { params: filters })
        if (response?.data?.success) {
          this.wheelsListData = response?.data?.data || []
          this.setWheelWeightStateSnapshot(this.wheelsListData)
          this.wheelsListMeta = this.getTableMeta(response?.data?.meta)

          if (response?.data?.filters) {
            this.appsOptions = response?.data?.filters?.applications.map(application => {
              return { name: application.name, value: application.id, platform: application.platform }
            })
            this.languageOptions = response?.data?.filters?.languages.map(language => {
              return { name: language.name, value: language.id }
            })

            this.typeOptions = response?.data?.filters?.wheel_types
              ? response.data.filters.wheel_types.map(type => {
                return { name: capitalizeFirstLetter(type), value: type }
              })
              : [
                { name: 'Authorized', value: 'authorized' },
                { name: 'Unauthorized', value: 'unauthorized' },
                { name: 'External', value: 'external' },
              ]

            this.segmentOptions = response?.data?.filters?.segments.map(segment => {
              return { name: segment.name, value: segment.id }
            })
          }
        }
      } catch (e) {
        console.log('Error when trying to get wheels of fortune', e)
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    resetAllFilters() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = undefined
      })
      this.$refs['wheel-filter'].resetFilters()
      this.fetchWheelsList()
    },
  },
  async mounted() {
    if (Object.keys(this.$route.query)?.length) {
      setFilters(this.$route.query, this, '', true)
      await waitForRef('wheel-filter', this)
        .then((filtersRef) => {
          filtersRef.setQueryFilters(this.$route.query)
        })
        .catch((err) => {
          console.warn('Не удалось получить $refs.filters:', err)
        })
    }
    const otherFilters = await this.getOtherFilters()

    this.fetchWheelsList({ ...this.filters, ...otherFilters })
  },
}
</script>

<style lang="scss">
.fortune-wheel-table {
  .wheel-link {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .ant-table-row {
    cursor: pointer;
  }
  &__edit-mode {
    .ant-table-row {
      cursor: default;
    }
  }
}
[data-kit-theme="default"] .ant-table-empty .ant-table-body {
  overflow: visible !important;
}
.ant-table-column-has-actions {
  overflow: visible !important;
}
</style>
